/* eslint-disable @typescript-eslint/no-explicit-any */
import { QueryParameters, WidgetQuerySource, WidgetTableSource } from '@carto/api-client'
import { call } from 'redux-saga/effects'
import { API_VERSIONS } from '@carto/react-api'
import { SourceFilters } from '@carto/react-redux'
import { isEqual } from 'lodash'
import settings from 'settings'
import {
  BuilderMode,
  KeplerLayer,
  KeplerLayerClasses,
  KeplerLayerConfig,
  KeplerTable,
  LayerRequest,
  LayerType,
  LayerTypes
} from 'features/builder'
import { RequestStatus, getToken } from 'features/common'
import { v4 as uuidv4 } from 'uuid'
import { ProviderType } from 'features/connections'
import { addCurrentMapDataset, getDatasetById, updateCurrentMapDataset } from 'features/maps/state/mapsSlice'
import { Formats, MapDataset, MapType } from 'features/maps/types'
import { addDataToMap, layerConfigChange, ProtoDataset } from '@carto/kepler.gl/actions'
import { put, select } from 'redux-saga/effects'
import { getDatasetLabel, getNextLayerLabel } from '../labels'
import { DEFAULT_TILE_RESOLUTION, KEPLER_MAP_CONFIG_VERSION } from 'features/builder/ui/builderConstants'
import { getkeplerDatasetById, getLayerByDatasetId, getLayersByDatasetId } from '../keplerGlSlice'
import { getLayerData } from 'features/builder/api/builder'
import { buildKeplerDataset } from './loaderUtils'
import {
  loadDataForWidgets,
  validateDatasetFilters,
  validateWidgetsConfig,
  applyCrossFilteringWidgetFilters,
  updateWidgetsGivenDatasetChanges
} from '../WidgetsSagas'
import { saveNewDatasetConfig, getDatasetSupportedLayerClasses, getNextDatasetColor } from '../datasetSagas'
import { RootState } from 'app/state/store'
import { Layer } from '@carto/kepler.gl/layers'
import { TILEJSON_FORMAT_TYPES } from 'features/builder/ui/builderConstants'
import { loadAttributeStatsRequest, loadHexColumnStatsRequest } from '../builderSlice'
import { layerTypeChange, layerVisualChannelConfigChange } from '@carto/kepler.gl/actions'
import {
  attributeHasStats,
  findPossibleLayerConfigsByDataset,
  shouldLoadAsDynamicTiling,
  getAttributesFromTilesetDataset,
  isServerSideMapFilteringSupported,
  getLayerConfigByLayerType,
  findFirstSuitableLayerConfigByDataset,
  getSchemaFromTilesetDataset
} from '../builderSliceUtils'
import { getClient } from 'features/common/utils/clientParameter'
import { guessGeoColumnFromSchema, parseGeoColumnSafe } from 'features/builder/utils/geoColumn'
import { patchDataset } from 'features/maps/api/maps'
import UnknownLayer from 'features/builder/ui/KeplerGl/layers/UnknownLayer'
import { validateSourceFilters } from '../widgetsUtils'
import { getAggregationExp } from '../spatialIndexSagas'
import { datasetSchemaEqual, getDatasetColumnsFromMap } from '../datasetUtils'
import { getDefaultAggregationExpForLayerType } from '../spatialIndexUtils'
import { loadKeplerVisLayerAttributeStats } from '../statsSagas'
import { updatePopupSettingsWithValidation } from '../popupSagas'
import { updateWidgetSource } from 'features/builder/state/widgets'
import { isAgentDataset } from 'features/builder/model/agent'

export interface LayerLoaderProps extends LayerRequest {
  datasetId?: string
  datasetLabel?: string
  datasetColor?: string
  fromAnalysis?: boolean
  minimized?: boolean
  createLayer?: boolean
}

export class LayerLoader {
  dataset?: ProtoDataset
  datasetId: string
  datasetLabel?: string
  datasetColor: string
  mapId: string
  source: string
  provider: ProviderType
  type: MapType
  format?: Formats
  geoColumn?: string | null
  columns?: string[] | null
  connection?: string
  accessToken?: string
  fromAnalysis?: boolean
  minimized?: boolean
  createLayer?: boolean
  aggregationExp?: string | null
  aggregationResLevel?: number | null
  queryParameters?: QueryParameters | null
  queryTemplate?: string | null
  filters?: SourceFilters | null
  alreadyTriedToGuessGeoColumn?: boolean

  constructor(props: LayerLoaderProps) {
    const {
      mapId,
      source,
      provider,
      type,
      connection,
      accessToken,
      format,
      geoColumn,
      columns,
      datasetId,
      datasetLabel,
      datasetColor,
      fromAnalysis,
      minimized,
      createLayer,
      aggregationExp,
      aggregationResLevel,
      queryParameters,
      queryTemplate,
      filters
    } = props
    this.source = source
    this.provider = provider
    this.type = type
    this.format = format
    this.geoColumn = geoColumn
    this.columns = columns
    this.connection = connection
    this.accessToken = accessToken
    this.datasetId = datasetId ? datasetId : uuidv4()
    this.datasetLabel = datasetLabel
    this.datasetColor = datasetColor || ''
    this.mapId = mapId
    this.fromAnalysis = fromAnalysis
    this.minimized = minimized
    this.createLayer = createLayer
    this.aggregationExp = aggregationExp
    this.aggregationResLevel = aggregationResLevel
    this.queryParameters = queryParameters
    this.queryTemplate = queryTemplate
    this.filters = filters
  }

  *getStatus() {
    try {
      const dataset = yield select(getDatasetById, this.datasetId)
      return dataset.status
    } catch (err) {
      const msg = `Cannot getStatus of ${this.datasetId}`
      throw new Error(msg)
    }
  }

  *datasetExists() {
    try {
      const datasetIsInMap = yield select(
        (state) => !!state.maps.currentMapDatasets.find((ds) => ds.id === this.datasetId)
      )
      return datasetIsInMap
    } catch (err) {
      throw new Error(`Cannot check existence of ${this.datasetId}`)
    }
  }

  *add() {
    try {
      if (!this.datasetColor) {
        this.datasetColor = yield getNextDatasetColor()
      }
      yield put(
        addCurrentMapDataset({
          id: this.datasetId,
          geoColumn: this.geoColumn,
          columns: this.columns,
          source: this.source,
          format: this.format,
          type: this.type,
          providerId: this.provider,
          connectionName: this.connection,
          accessToken: this.accessToken,
          label: this.datasetLabel,
          color: this.datasetColor,
          status: RequestStatus.Pending,
          errorMessage: undefined
        })
      )
      const layerAdded = yield this._add()
      if (layerAdded) {
        // Mark the dataset as ready
        yield put(
          updateCurrentMapDataset({
            id: this.datasetId,
            dataset: {
              status: RequestStatus.Fulfilled,
              errorMessage: undefined
            }
          })
        )
      }

      yield loadDataForWidgets(yield this.getCartoData(), this.dataset as unknown as KeplerTable)

      // Whenever we add a Data Source, there might be Widgets applying Cross-Filtering that might affect it
      yield applyCrossFilteringWidgetFilters(this.datasetId)
    } catch (error) {
      yield put(
        updateCurrentMapDataset({
          id: this.datasetId,
          dataset: {
            status: RequestStatus.Rejected,
            errorMessage: (error as Error).message
          }
        })
      )
    }
  }

  *getKeplerDataset(headers: Record<string, string> = {}) {
    try {
      const keplerDataset = yield this._fetchData(headers)
      // Mark the dataset as ready
      yield put(
        updateCurrentMapDataset({
          id: this.datasetId,
          dataset: {
            status: RequestStatus.Fulfilled
          }
        })
      )
      return keplerDataset
    } catch (error) {
      yield put(
        updateCurrentMapDataset({
          id: this.datasetId,
          dataset: {
            status: RequestStatus.Rejected,
            errorMessage: (error as Error).message
          }
        })
      )
    }
  }

  *_add(): Generator<any, any, any> {
    yield this._saveDataset()
    yield this._fetchData()
    // Check that the dataset hasn't been deleted by the user during the loading
    const datasetStillExists = yield this.datasetExists()
    if (!datasetStillExists) {
      return false
    }

    yield this._addDataset()

    if (this.createLayer) {
      yield this._createLayer()
    }

    return true
  }

  *_saveDataset(): Generator<any, any, any> {
    try {
      if (isAgentDataset(this.datasetId)) {
        return
      }

      const previousId = this.datasetId
      // add the source to the storage
      this.datasetLabel = !this.datasetLabel ? yield getDatasetLabel(this.source, this.type) : this.datasetLabel
      const savedDataset = yield saveNewDatasetConfig({
        provider: this.provider,
        type: this.type,
        source: this.source,
        columns: this.columns,
        format: this.format,
        geoColumn: this.geoColumn,
        connection: this.connection,
        label: this.datasetLabel as string,
        color: this.datasetColor as string,
        queryTemplate: this.queryTemplate,
        queryParameters: this.queryParameters
      })
      this.datasetId = savedDataset.id
      // add the source to the storage
      yield put(
        updateCurrentMapDataset({
          id: previousId,
          dataset: {
            id: this.datasetId,
            label: this.datasetLabel,
            connectionId: savedDataset.connectionId,
            color: this.datasetColor,
            runningSince: Date.now(),
            runningTo: undefined,
            queryTemplate: this.queryTemplate,
            queryParameters: this.queryParameters
          }
        })
      )
    } catch (error) {
      throw new Error(`Error saving dataset at server ${(error as Error).message}`)
    }
  }

  *_fetchData(headers: Record<string, string> = {}, { dontRetry = false } = {}): Generator<any, any, any> {
    let data, schema, sourceOptions

    const {
      source,
      type,
      connection,
      accessToken,
      provider,
      format,
      geoColumn,
      columns,
      aggregationExp,
      aggregationResLevel,
      queryParameters,
      mapId
    } = this

    let filters: SourceFilters | undefined
    const canRetryWithDefaultGeoColumn = !dontRetry

    try {
      if (this.filters && isServerSideMapFilteringSupported({ source: yield this.getCartoData() })) {
        filters = this.filters
      }

      // fetch the data from Maps API
      ;({ data, schema, sourceOptions } = yield call(getLayerData, {
        source,
        type,
        connection,
        accessToken,
        provider,
        format,
        ...(columns && { columns }),
        ...(geoColumn && { geoColumn }),
        ...(aggregationExp && { aggregationExp }),
        ...(typeof aggregationResLevel === 'number' && { aggregationResLevel }),
        ...(queryParameters && { queryParameters }),
        filters,
        headers: headers,
        client: getClient(),
        expectErrorWithSchema: true,
        mapId
      }))

      // WidgetSource class is not serializable, keep separate from tilejson.
      if (data?.widgetSource) {
        yield updateWidgetSource(this.datasetId, data.widgetSource)
        data = { ...data }
        delete data.widgetSource
      } else if (schema && !data && sourceOptions) {
        const widgetSourceOptions = {
          spatialDataColumn: 'geo',
          connectionName: sourceOptions.connection,
          tileResolution: DEFAULT_TILE_RESOLUTION,
          ...sourceOptions
        }
        const widgetSource =
          type === 'table'
            ? new WidgetTableSource({
                ...widgetSourceOptions,
                tableName: sourceOptions.source
              })
            : new WidgetQuerySource({
                ...widgetSourceOptions,
                sqlQuery: sourceOptions.source
              })
        yield updateWidgetSource(this.datasetId, widgetSource)
      }

      let retry = false
      if (filters && schema) {
        // it's possible that old filters contain bad column, if this is the case we need to remove them
        // and retry the request
        // note, that map instantiation with wrong filters usually don't fail, only subsequent tile requests will
        // fail if filters are using old/outdated columns, so we need to retry the request even without any error thrown now
        const [filtersValid, newValidFilters] = validateSourceFilters(filters, {
          schema,
          providerId: provider
        })
        if (!filtersValid) {
          this.filters = newValidFilters
          retry = true
        }
      }

      if (!this.alreadyTriedToGuessGeoColumn && (!geoColumn || (schema && !data))) {
        const newGuessedGeoColumn = guessGeoColumnFromSchema(schema)
        if (newGuessedGeoColumn) {
          this.geoColumn = newGuessedGeoColumn
          this.alreadyTriedToGuessGeoColumn = true
          retry = true
        }
      }
      if (retry) {
        return yield this._fetchData(headers)
      }

      if (data && this.alreadyTriedToGuessGeoColumn) {
        yield this.updateMapDataset({ geoColumn: this.geoColumn })
      }
    } catch (error: any) {
      if (canRetryWithDefaultGeoColumn && String(error.message).includes('Invalid spatial index column')) {
        // that means user managed to save completly bogus column, reset it to `geom` and try to get schema
        this.geoColumn = 'geom'
        this.alreadyTriedToGuessGeoColumn = true
        return yield this._fetchData(headers, { dontRetry: true })
      }
      throw new Error(error)
    }
    try {
      this.dataset = buildKeplerDataset({
        id: this.datasetId,
        label: this.datasetLabel as string,
        color: this.datasetColor as string,
        format,
        connection,
        data,
        type: this.type,
        source,
        schema,
        provider: this.provider,
        geoColumn,
        columns,
        aggregationExp,
        aggregationResLevel,
        accessToken: this.accessToken,
        ...(queryParameters && { queryParameters })
      })

      if (!this.dataset?.data) {
        throw new Error(`No geometry data detected at your table.`)
      }

      return this.dataset
    } catch (error) {
      throw new Error(`Error building kepler dataset. ${(error as Error).message}`)
    }
  }

  *getCartoData() {
    const token = this.accessToken || (yield getToken())
    const queryParameters = this.queryParameters
    const { spatialDataType, spatialDataColumn } = parseGeoColumnSafe(this.geoColumn)

    return {
      id: this.datasetId,
      data: this.source,
      ...(queryParameters && { queryParameters }),
      type: this.type,
      connection: this.connection,
      provider: this.provider,
      geoColumn: this.geoColumn,
      spatialDataType,
      spatialDataColumn,
      aggregationResLevel: this.aggregationResLevel,
      credentials: {
        apiVersion: API_VERSIONS.V3,
        accessToken: token,
        apiBaseUrl: settings.apiBaseUrl
      },
      filters: this.filters
    }
  }

  *_addDataset() {
    if (!this.dataset) {
      throw new Error('Dataset not initialized. Please call before to saveDataset')
    }

    try {
      const options = { autoCreateLayers: false, keepExistingConfig: true, centerMap: false }

      yield put(
        addDataToMap({
          datasets: [this.dataset],
          options
        })
      )
    } catch (error) {
      console.error('LayerLoader#_addDataset failed', error)
      throw new Error(`Error adding dataset to map. ${(error as Error).message}.`, { cause: error })
    }
  }

  *_createLayer() {
    if (!this.dataset) {
      throw new Error('Dataset not initialized. Please call before to saveDataset')
    }

    try {
      const label = yield getNextLayerLabel()
      const options = { autoCreateLayers: false, keepExistingConfig: true, centerMap: true }

      const keplerDataset = yield select((state) => state.keplerGl.map.visState.datasets[this.datasetId])
      const layerClasses: KeplerLayerClasses = yield this._getLayerClasses()

      const layerConfig = findFirstSuitableLayerConfigByDataset({ dataset: keplerDataset, layerClasses, label })
      if (!layerConfig) {
        throw new Error('Cannot load layer. Please make sure this layer has geometry data')
      }

      // Create the layer in kepler. Note dataset was added before
      yield put(
        addDataToMap({
          datasets: [],
          options,
          config: { version: KEPLER_MAP_CONFIG_VERSION, visState: { layers: [layerConfig] } }
        })
      )

      // By default the layer use the dataset label, the following two lines allow us to change it.
      const currentLayer: KeplerLayer = yield select(getLayerByDatasetId, this.datasetId)
      yield put(
        layerConfigChange(currentLayer, {
          label: label,
          // not sure why initial layer config is not passed, so we need to ensure that LayerPanel
          // is always open for new UnknownLayer
          ...(currentLayer instanceof UnknownLayer && { isConfigActive: true })
        })
      )

      if (typeof currentLayer.getNewLayerConfig === 'function' && keplerDataset.dataContainer) {
        const layers: KeplerLayer[] = yield select((state) => state.keplerGl.map.visState.layers)
        yield put(layerConfigChange(currentLayer, currentLayer.getNewLayerConfig(keplerDataset, layers)))
      }
    } catch (error) {
      console.error('LayerLoader#_createLayer failed', error)
      throw new Error(`Error creating layer. ${(error as Error).message}.`, { cause: error })
    }
  }

  *_refreshLayers(forceLayerType?: LayerType) {
    if (!this.dataset || !this.datasetId) {
      throw new Error('Not dataset initialized. Please call before to saveDataset')
    }

    try {
      const options = { autoCreateLayers: false, keepExistingConfig: true, centerMap: false }
      yield put(
        addDataToMap({
          datasets: [this.dataset],
          options
        })
      )

      const keplerDataset = yield select((state) => state.keplerGl.map.visState.datasets[this.datasetId as string])

      if (!keplerDataset) return

      const layerClasses: KeplerLayerClasses = yield this._getLayerClasses()

      if (forceLayerType) {
        const layerConfig = getLayerConfigByLayerType({
          layerType: forceLayerType,
          dataset: keplerDataset,
          layerClasses
        })
        if (!layerConfig) throw new Error(`Unable to find a layer config for: ${forceLayerType}`)
        yield updateLayersConfig(this.datasetId, [layerConfig])
      } else {
        const layerConfigs = findPossibleLayerConfigsByDataset({ dataset: keplerDataset, layerClasses })
        if (layerConfigs.length === 0) throw new Error('Unable to find any suitable layer config')
        yield updateLayersConfig(this.datasetId, layerConfigs)
      }

      yield validateWidgetsConfig(this.datasetId)
      yield validateDatasetFilters(this.datasetId)
    } catch (error) {
      throw new Error(`Error adding new query to map. ${(error as Error).message}`)
    }
  }

  *_getLayerClasses() {
    return yield getDatasetSupportedLayerClasses({ type: this.type, format: this.format })
  }

  *_refreshStats(oldDataset: KeplerTable, noCache?: boolean) {
    try {
      if (!this.dataset?.info?.id) {
        return
      }
      const newDataset = this.dataset as unknown as KeplerTable // TODO
      {
        // normal stats
        const oldAttributes = getAttributesFromTilesetDataset(oldDataset)
        const attributeStatsToLoad = getAttributesFromTilesetDataset(newDataset).filter((newAttr) =>
          attributeHasStats(oldAttributes.find((oldAttr) => newAttr.attribute === oldAttr.attribute))
        )

        for (const attr of attributeStatsToLoad) {
          yield put(loadAttributeStatsRequest({ datasetId: this.dataset.info.id, attribute: attr.attribute, noCache }))
        }
      }
      {
        // hex column stats (extended)
        const oldHexColumnStats = oldDataset.metadata?.hexColumnStats || {}
        const hexColumnStatsToRefresh = Object.keys(newDataset.metadata.hexColumnStats || {}).filter(
          (newColumn) => newColumn in oldHexColumnStats
        )

        for (const column of hexColumnStatsToRefresh) {
          yield put(loadHexColumnStatsRequest({ datasetId: this.dataset.info.id, attribute: column, noCache }))
        }
      }
    } catch (error) {
      throw new Error(`Error getting stats. ${(error as Error).message}`)
    }
  }

  *_refreshKeplerVisLayerStats(noCache?: boolean) {
    // eslint-disable-next-line redux-saga/no-unhandled-errors
    const layers: Layer[] = yield select(getLayersByDatasetId, this.datasetId)
    for (const layer of layers) {
      yield loadKeplerVisLayerAttributeStats({ layer, noCache })
    }
  }

  *_refreshDatasetColumnsAndAggregationExp({
    forceLayerType,
    noCache
  }: {
    forceLayerType?: LayerType
    noCache?: boolean
  }) {
    try {
      if (!this.dataset?.metadata?.schema || !this.datasetId) {
        return
      }
      const fields = this.dataset.metadata.schema.map((f) => f.name) as string[] | undefined

      if (!fields) {
        return
      }

      let aggregationExp = null

      if (this.aggregationExp) {
        aggregationExp = forceLayerType
          ? getDefaultAggregationExpForLayerType(forceLayerType, this.provider, this.dataset.metadata.schema)
          : yield getAggregationExp(this.datasetId, fields)
      }

      const columns = yield getDatasetColumnsFromMap(this.datasetId, fields)

      if (aggregationExp !== this.aggregationExp || !isEqual(columns, this.columns)) {
        this.aggregationExp = aggregationExp
        this.columns = columns
        yield this._fetchDataWithCacheControl(noCache)
        yield this.updateMapDataset({ columns, aggregationExp })
      }
    } catch (error) {
      throw new Error(`Error refreshing the aggregation expression and columns ${(error as Error).message}`)
    }
  }

  *_fetchDataWithCacheControl(noCache?: boolean) {
    yield this._fetchData({
      ...(noCache && { 'Cache-Control': 'no-cache' })
    })
  }

  *refreshDataset({
    forceLayerType,
    noCache
  }: {
    forceLayerType?: LayerType
    noCache?: boolean
  }): Generator<any, any, any> {
    try {
      const mapDataset: MapDataset = yield select((state: RootState) => getDatasetById(state, this.datasetId))
      const oldDataset = yield select((state: RootState) => getkeplerDatasetById(state, this.datasetId))
      // oldDataset could not exist because it failed during the map load
      const keplerDatasetWasLoaded = !!oldDataset
      const oldSchema = getSchemaFromTilesetDataset(oldDataset)
      const loadAsDynamicTiling = shouldLoadAsDynamicTiling({ type: this.type, format: mapDataset.format })
      const lastRefreshAt = new Date().getTime()

      yield put(
        updateCurrentMapDataset({
          id: this.datasetId,
          dataset: {
            status: RequestStatus.Pending,
            errorMessage: undefined,
            runningSince: Date.now(),
            runningTo: undefined,
            lastRefreshAt
          }
        })
      )

      yield this._fetchDataWithCacheControl(noCache)

      if (!this.dataset || !this.datasetId) throw new Error('Not dataset initialized')

      if (!keplerDatasetWasLoaded) {
        // We load the new Kepler dataset using the result of _fetchDataWithCacheControl.
        // This is necessary because some of the following code, such as _refreshDatasetColumnsAndAggregationExp,
        // depends on the Kepler dataset being available.
        yield put(
          addDataToMap({
            datasets: [this.dataset],
            options: { autoCreateLayers: false, keepExistingConfig: true, centerMap: false }
          })
        )
      }

      const newDataset = yield select((state: RootState) => getkeplerDatasetById(state, this.datasetId))
      const newSchema = getSchemaFromTilesetDataset(newDataset)
      const schemaChanged = !datasetSchemaEqual(oldSchema, newSchema)

      yield this._refreshDatasetColumnsAndAggregationExp({ noCache, forceLayerType })
      yield this._refreshLayers(forceLayerType)

      if (loadAsDynamicTiling) {
        // If the Kepler dataset failed to load during the map initialization, _refreshStats will not work
        // because it relies on the oldDataset to determine which stats to request. In this case, we read the stats directly from the layers
        keplerDatasetWasLoaded
          ? yield this._refreshStats(oldDataset, noCache)
          : yield this._refreshKeplerVisLayerStats(noCache)
      }

      yield put(
        updateCurrentMapDataset({
          id: this.datasetId,
          dataset: {
            status: RequestStatus.Fulfilled,
            runningTo: Date.now(),
            errorMessage: undefined
          }
        })
      )

      // We call loadDataForWidgets because the refreshDataset can be called because the datasource has changed
      yield loadDataForWidgets(yield this.getCartoData(), this.dataset as unknown as KeplerTable)

      if (schemaChanged) {
        // Dataset might have changed their columns, so we need to validate the Popup Settings
        yield updatePopupSettingsWithValidation(this.datasetId)

        // Dataset might have change, so we need to update the cross-filtering widgets
        yield updateWidgetsGivenDatasetChanges(this.datasetId)
      }
    } catch (error) {
      yield put(
        updateCurrentMapDataset({
          id: this.datasetId,
          dataset: {
            status: RequestStatus.Rejected,
            errorMessage: (error as Error).message,
            runningTo: Date.now()
          }
        })
      )
    }
  }
  *updateMapDataset(update: Partial<MapDataset>) {
    try {
      const builderMode: BuilderMode = yield select((state: RootState) => state.builder.builderMode)
      if (builderMode === BuilderMode.Editor && !isAgentDataset(this.datasetId)) {
        yield patchDataset({
          datasetId: this.datasetId,
          mapId: this.mapId,
          body: update
        })
      }
      yield put(
        updateCurrentMapDataset({
          id: this.datasetId,
          dataset: update
        })
      )
    } catch (error) {
      throw new Error(`layerLoader#updateMapDataset failed: ${error}`, { cause: error })
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* updateLayersConfig(
  dataId: string,
  layerConfigs: { id: string; type: LayerType; config: Record<string, any> }[]
) {
  try {
    const layersByDataId: Array<Layer> = yield select((state) =>
      state.keplerGl.map.visState.layers.filter((layer: Layer) => layer.config.dataId === dataId)
    )

    for (const layer of layersByDataId) {
      // Use current Layer config, or find most suitable one depending on the current layer
      const layerConfig = layerConfigs.find(({ type }) => type === layer.type) || layerConfigs[0]

      const newRequiredColumns = Object.keys(layerConfig.config.columns)
      const isTilejsonType = TILEJSON_FORMAT_TYPES.includes(layerConfig.type)
      const layerRequiredColumns = Object.keys(layer.config.columns).filter((c) => !layer.config.columns[c].optional)
      let newLayer: Layer

      if (
        // Tilejson layers have not required columns
        (isTilejsonType && layer.type !== layerConfig.type) ||
        !newRequiredColumns.every(
          (elem) =>
            layerRequiredColumns.includes(elem) &&
            layerConfig.config.columnsIndexed[elem].fieldIdx === layer.config.columns[elem].fieldIdx
        )
      ) {
        const idx: number = yield select((state) =>
          state.keplerGl.map.visState.layers.findIndex((l: Layer) => l.id === layer.id)
        )

        yield put(layerTypeChange(layer, layerConfig.type))
        // When you change the layer type a new layer instace is created so you need to get the layer again using idx
        newLayer = yield select((state) => state.keplerGl.map.visState.layers[idx])

        const layerConfigUpdate: Partial<KeplerLayerConfig> = {
          columns: layerConfig.config.columnsIndexed
        }
        if (layer.type === LayerTypes.Unknown) {
          layerConfigUpdate.isVisible = true
        }
        yield put(layerConfigChange(newLayer, layerConfigUpdate))
      } else {
        // Force layer rendering anyway
        yield put(layerConfigChange(layer, layer.config))
        // Retrieve updated bounds
        newLayer = yield select((state) => state.keplerGl.map.visState.layers.find((l: Layer) => l.id === layer.id))
      }

      yield validateVisualChannels(layer)
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    throw new Error(e)
  }
}

function* validateVisualChannels(layer: Layer) {
  try {
    const dataset = yield select(getkeplerDatasetById, layer.config.dataId as string)

    if (!dataset) {
      return
    }

    for (const channel of Object.keys(layer.visualChannels)) {
      const { field } = layer.visualChannels[channel]
      const stylingPropertyNotExist = Boolean(
        layer.config[field] && !dataset.fields.some((f) => f.id === layer.config[field].id)
      )

      if (stylingPropertyNotExist) {
        yield put(layerVisualChannelConfigChange(layer, { [field]: null }, channel))
      }
    }
  } catch (error) {
    throw new Error(`Error updating layer styles. ${(error as Error).message}`)
  }
}
